import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useNuxtApp } from 'nuxt/app';
import { useToast } from 'primevue/usetoast';
import { z } from 'zod';
import type { EnterUsernameForm } from '~/types/auth/forms/ForgotPasswordForm';
import type { ApiInstance } from '~/types/plugins/api';
import { EnterUsername } from '~/utils/contents/auth/forms/ForgotPasswordForm';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useSendForgotPasswordCode() {
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const form = ref<EnterUsernameForm>(getInitialState());

  const toast = useToast();

  const validations = z
    .object({
      email: z
        .string()
        .min(1, {
          message: `${EnterUsername.fields.email.label} is required.`,
        })
        .email({
          message: `${EnterUsername.fields.email.label} must be a valid email.`,
        }),
    })
    .partial();
  const resolver = zodResolver(validations);

  const submit = async (): Promise<boolean> => {
    if (loading.value) return false;

    if (!validations.safeParse(form.value).success) {
      return false;
    }

    loading.value = true;

    try {
      const { $apiClient } = useNuxtApp() as unknown as {
        $apiClient: ApiInstance;
      };

      const result = await $apiClient.post<{
        error?: {
          data?: {
            errors?: Record<string, string[]>;
          };
        };
      }>(
        'auth/forgot-password',
        { username: form.value.email },
        { skipAuth: true },
      );

      if (result.error) {
        errors.value = result.error.data?.errors || {};
        return false;
      } else {
        return true;
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: $parseErrorMessage(error),
        life: 3000,
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const reset = (): void => {
    form.value = getInitialState();
  };

  function getInitialState(): EnterUsernameForm {
    return {
      email: '',
    };
  }

  return {
    loading,
    form,
    errors,
    validations,
    resolver,

    submit,
    reset,
  };
}
